import { createContext, useState, ReactNode, useEffect, useContext, useLayoutEffect, useRef } from 'react';
import apiClient from '../api';
import { IUser } from '../models/user';
import { AxiosHeaders } from 'axios';

// const localStoragekey = process.env.REACT_APP_LOCALSTORAGE_KEY as string;
export interface UserRequestBody {
	firstName?: string,
	lastName?: string,
	orgName?: string
	email?: string,
	password?: string,  
	newPassword?: string
	restoreAccount?: boolean,
}

// context type
interface AuthContextType {
	user?: IUser | null;
	authToken?: string | null;
	loginUser: (body: UserRequestBody) => Promise<void>;
	updateUser: (body: UserRequestBody) => Promise<void>;
	registerUser: (body: UserRequestBody) => Promise<void>;
	updateUserEmail: (body: UserRequestBody) => Promise<void>;
	updateUserPassword: (body: UserRequestBody) => Promise<void>;
	updateUserPicture: (body: FormData) => Promise<void>;
	cancelEmailChange: () => Promise<void>
	deleteUser: () => Promise<void>;
	logoutUser: () => void;
	loading: boolean;
}

// initialize context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// context provide
const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [authToken, setAuthToken] = useState<string | null>()
	const [user, setUser] = useState<IUser | null>();
	const [loading, setLoading] = useState(true);

	// fetch user on mount
	useEffect(() => {
		const loadUser = async () => {
			try {
				const {data} = await apiClient.get<IUser>("/auth/profile");
				setUser(data);
			} catch (error) {
				setUser(null);
			} finally {
				setLoading(false);
			}
		};

		loadUser();
	}, []);

	// create request interceptor to attach token
	useLayoutEffect(() => {
		const requestInterceptor = apiClient.interceptors.request.use(
			(config) => {
				if (authToken) {
					// Ensure headers is initialized
					if (!config.headers) {
						config.headers = new AxiosHeaders();
					}

					// Attach the token to the Authorization header
					config.headers.set('Authorization', `Bearer ${authToken}`);

				}
				return config;
			}
		);

		return () => {
			apiClient.interceptors.request.eject(requestInterceptor);
		};
	}, [authToken]);



	// if refresh token is in progress, queue requests
	const isRefreshing = useRef(false);
	const failedQueue = useRef<any[]>([]);

	// function to process/retry the queued requests
	const processQueue = (error: any, token: string | null = null) => {
		failedQueue.current.forEach(prom => {
			if (error) {
				prom.reject(error);
			} else {
				prom.resolve(token);
			}
		});
		failedQueue.current = [];
	};

	// create response interceptor to handle 401 errors
	useLayoutEffect(() => {
		const responseInterceptor = apiClient.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;
				// console.log("Response Error:", error?.response?.status, error?.response?.data?.name, originalRequest?.url);
				if (
					error?.response?.status === 401 && 
					error?.response?.data?.name === "UnauthorizedError" && 
					!originalRequest._retry
				) {

					if (isRefreshing.current) {
						// Queue the request while refreshing
						return new Promise((resolve, reject) => {
							failedQueue.current.push({
								resolve: (token: string) => {
									originalRequest.headers.Authorization = `Bearer ${token}`;
									resolve(apiClient(originalRequest));
								},
								reject: (err: any) => {
									reject(err);
								}
							});
						});
					}

					originalRequest._retry = true;
					isRefreshing.current = true;

					// try refreshing token
					try {
						// console.log("Token refresh in progress for", originalRequest.url);
						const {data} = await apiClient.post<{user: IUser, token: string}>("/auth/refresh-token", {}, {withCredentials: true});
						// localStorage.setItem(localStoragekey, data.token);
						setAuthToken(data.token);
						setUser(data.user);

						// console.log("Token refresh successful for", originalRequest.url);
						originalRequest.headers.Authorization = `Bearer ${data.token}`;
						processQueue(null, data.token); // resolve all queued requests
						return apiClient(originalRequest); // retry original request with new token
					}
					catch (err) {
						// console.log("Token refresh failed. Logging out...");
						// localStorage.removeItem(localStoragekey);
						processQueue(err, null); // reject all queued requests
						setAuthToken(null);
						setUser(null);
						return Promise.reject(err);
					} finally {
						isRefreshing.current = false; // reset refreshing state
					}
				}
				return Promise.reject(error);
			}
		);
		return () => {
			apiClient.interceptors.response.eject(responseInterceptor);
		};
	}, []);

	const registerUser = async (body: UserRequestBody) => {
		const {data} =await apiClient.post<{user: IUser, token: string}>("/auth/register", body);
		// localStorage.setItem(localStoragekey, data.token);
		setAuthToken(data.token);
		setUser(data.user);
	};

	const loginUser = async (body: UserRequestBody) => {
		const {data} = await apiClient.post<{user: IUser, token: string}>("/auth/login", body);
		// localStorage.setItem(localStoragekey, data.token);
		setAuthToken(data.token);
		setUser(data.user);
	};

	const logoutUser = async () => {
		await apiClient.post("/auth/logout", {}, {withCredentials: true});
		// localStorage.removeItem(localStoragekey);
		setAuthToken(null);
		setUser(null);
	};

	const updateUser = async (body: UserRequestBody) => {
		const {data} = await apiClient.patch<IUser>(`/auth/`, body);
		setUser(data);
	}
	
	const updateUserEmail = async (body: UserRequestBody) => {
		const {data} = await apiClient.patch<IUser>(`/auth/email/`, body);
		setUser(data);
	}

	const cancelEmailChange = async () => {
		const {data} = await apiClient.patch<IUser>(`/auth/email/cancel`);
		setUser(data);
	}
	
	const updateUserPicture = async (body: FormData) => {
		const {data} = await apiClient.patch<IUser>(`/auth/picture/`, 
			body, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
		setUser(data);
	}
	
	const updateUserPassword = async (body: UserRequestBody) => {
		const {data} = await apiClient.patch<IUser>(`/auth/password/`, body);
		setUser(data);
	}

	const deleteUser = async () => {
		await apiClient.delete(`/auth/`);
		logoutUser();
	}

	return (
		<AuthContext.Provider value={{
				user,
				authToken,
				loginUser, 
				logoutUser,
				updateUser,
				registerUser,
				updateUserEmail,
				updateUserPicture,
				updateUserPassword,
				cancelEmailChange,
				deleteUser,
				loading
			}}>
			{children}
		</AuthContext.Provider>
	);
};

// A hook to use context
export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
	  throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export default AuthProvider;
